"use client";

import React, { useState } from "react";
import Image from "next/image";
import { AdSlot } from "@/features/ads/AdSlot";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import { ArticleCarousel } from "@/views/home/partials/articles/ArticleCarousel";
import { LatestArticlesSidebar } from "@/views/home/partials/articles/LatestArticlesSidebar";
import { CategorySection } from "@/views/home/parts/CategorySection";
import { PromoSection } from "@/views/home/parts/PromoSection";
import { SectionStrip } from "@/components/layout/SectionStrip";
import type { BrandedStyle } from "@/components/layout/SectionStrip";
import { HOME_HERO, HOME_COMPETITIONS } from "@/features/theme/sectionKeys";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { NewsBlogHomeClassicData, NewsBlogHomeSection } from "@/views/home/variants/NewsBlogHomepage/Classic/types";
import type { ResolvedPromoSlot } from "@/features/promo-slots/types";

export interface BasicHomeEditorialLayoutProps {
    data: NewsBlogHomeClassicData;
    videoShortsSlot?: React.ReactNode;
    videoHighlightsSlot?: React.ReactNode;
    sectionStyles?: Record<string, BrandedStyle>;
}

/**
 * BasicHomeEditorialLayout — Magazine-style homepage for the Basic site type.
 *
 * Content-only layout — no livescore ticker, livescore widget, or competition sections.
 * Competition-kind items in `data.sections` are silently skipped (defensive for cloned tenants).
 *
 * Sections (top to bottom):
 * 1. Article Carousel (up to 5 featured) + Latest Articles sidebar
 * 2. Video Shorts carousel
 * 3. Category / promo content stream (tabbed for category runs, inline for promos)
 * 4. Video Highlights carousel
 */
export function BasicHomeEditorialLayout({
    data,
    videoShortsSlot,
    videoHighlightsSlot,
    sectionStyles,
}: BasicHomeEditorialLayoutProps) {
    const labels = useLabels();

    // Filter to content-only sections (silently skip competition items)
    const contentSections = data.sections.filter((s) => s.kind === "category" || s.kind === "promo");

    // Carousel: featured posts first, fallback to latest
    const carouselArticles =
        data.featuredPosts.length > 0 ? data.featuredPosts.slice(0, 5) : data.latestNews.slice(0, 5);

    // Sidebar: latest articles (no overlap with carousel when using featured)
    const latestStartIndex = data.featuredPosts.length > 0 ? 0 : 5;
    const sidebarArticles = data.latestNews.slice(latestStartIndex, latestStartIndex + 7);

    return (
        <div className="min-h-screen bg-background">
            <main className="max-w-350 mx-auto px-4 py-6 space-y-10">
                {/* Ad: content-top */}
                <AdSlot id="content-top" />

                {/* Hero: Article Carousel + Latest Sidebar */}
                {carouselArticles.length > 0 && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_HERO]}>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                            <div className="lg:col-span-2">
                                <ArticleCarousel articles={carouselArticles} />
                            </div>
                            <div className="lg:col-span-1">
                                <LatestArticlesSidebar articles={sidebarArticles} title={labels.homePage.latestNews} />
                            </div>
                        </div>
                    </SectionStrip>
                )}

                {/* Promo slot 0 — global / page-type override */}
                <PromoSlot row={data.promos[0] ?? null} />

                {/* Video Shorts */}
                {videoShortsSlot}

                {/* Ad: mid-scroll-1 */}
                <AdSlot id="mid-scroll-1" />

                {/* Content stream — category / promo sections (competition items skipped) */}
                {contentSections.length > 0 && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_COMPETITIONS]}>
                        <BasicEditorialContentStream sections={contentSections} />
                    </SectionStrip>
                )}

                {/* Ad: mid-scroll-2 */}
                <AdSlot id="mid-scroll-2" />

                {/* Video Highlights */}
                {videoHighlightsSlot}

                {/* Promo slot 1 — global / page-type override */}
                <PromoSlot row={data.promos[1] ?? null} />

                {/* Ad: content-bottom */}
                <AdSlot id="content-bottom" />
            </main>
        </div>
    );
}

type ContentSection = Extract<NewsBlogHomeSection, { kind: "category" | "promo" }>;

type EditorialBlock =
    | { kind: "promo"; promo: ResolvedPromoSlot }
    | { kind: "tabs"; categories: Extract<ContentSection, { kind: "category" }>[] };

/**
 * Split content sections into renderable blocks. Promos break the tab run —
 * `[cat, cat, promo, cat]` becomes `[tabs(cat, cat), promo, tabs(cat)]`.
 */
function chunkSectionsByPromo(sections: ContentSection[]): EditorialBlock[] {
    const blocks: EditorialBlock[] = [];
    let buffer: Extract<ContentSection, { kind: "category" }>[] = [];
    const flush = () => {
        if (buffer.length > 0) {
            blocks.push({ kind: "tabs", categories: buffer });
            buffer = [];
        }
    };
    for (const section of sections) {
        if (section.kind === "promo") {
            flush();
            blocks.push({ kind: "promo", promo: section.data });
        } else if (section.kind === "category") {
            buffer.push(section);
        }
    }
    flush();
    return blocks;
}

function BasicEditorialContentStream({ sections }: { sections: ContentSection[] }) {
    const blocks = chunkSectionsByPromo(sections);
    if (blocks.length === 0) return null;

    return (
        <div className="space-y-10">
            {blocks.map((block, i) => {
                if (block.kind === "promo") {
                    return <PromoSection key={`promo-${i}`} promo={block.promo} />;
                }
                return <TabbedCategorySections key={`tabs-${i}`} categories={block.categories} />;
            })}
        </div>
    );
}

function TabbedCategorySections({ categories }: { categories: Extract<ContentSection, { kind: "category" }>[] }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const active = categories[activeIndex];
    if (!active) return null;

    return (
        <section>
            {/* Tab bar — only render when there are multiple categories */}
            {categories.length > 1 && (
                <div className="flex gap-1 overflow-x-auto pb-2 mb-2 scrollbar-hide">
                    {categories.map((section, index) => {
                        const label = section.data.title;
                        const logo = section.data.badgeUrl;
                        const key = `cat-${section.data.categoryId}-${index}`;
                        return (
                            <button
                                key={key}
                                onClick={() => setActiveIndex(index)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors shrink-0 cursor-pointer ${
                                    index === activeIndex
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                                }`}
                            >
                                {logo && (
                                    <Image
                                        src={logo}
                                        alt={label}
                                        width={20}
                                        height={20}
                                        className="w-5 h-5 rounded-sm object-contain"
                                    />
                                )}
                                {label}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Active category section */}
            <CategorySection data={active.data} />
        </section>
    );
}
